import { useState } from 'react';
import { useQueryClient, useQuery, useMutation } from '@tanstack/react-query';
import { User, Team } from '@tassomutuo-backoffice/types';
import { toast } from 'react-toastify';
import {
  changeCanReceiveLeads,
  changeCanReceiveLeadsQuinto,
  changeCanReceiveLeadsPrestito,
  getUsers,
} from '../../../../services/api/users/user';
import {
  promoteUserToTeamManager,
  removeUserFromTeamManager,
  getAllTeams,
  addMemberToTeam,
  removeMemberFromTeam,
} from '../../../../services/api/team/team';

type ModalConfig = {
  title: string;
  description: string;
  onConfirm: () => Promise<void>;
};

type ModalState = {
  userToChangeTeamManager: User | null;
  addMemberToTeam: { team: Team; user: User } | null;
  removeMemberFromTeam: { team: Team; user: User } | null;
  userToChangeCanReceiveLeads: User | null;
  userToChangeCanReceiveLeadsQuinto: User | null;
  userToChangeCanReceiveLeadsPrestito: User | null;
};

const defaultPageModel = {
  page: 0,
  pageSize: 10,
};

const useTeamsPage = () => {
  const queryClient = useQueryClient();

  const [tableState, setTableState] = useState({
    pagination: defaultPageModel,
    filterEmail: '',
  });

  const [modalState, setModalState] = useState<ModalState>({
    userToChangeTeamManager: null as User | null,
    addMemberToTeam: null as { team: Team; user: User } | null,
    removeMemberFromTeam: null as { team: Team; user: User } | null,
    userToChangeCanReceiveLeads: null as User | null,
    userToChangeCanReceiveLeadsQuinto: null as User | null,
    userToChangeCanReceiveLeadsPrestito: null as User | null,
  });

  const {
    isPending,
    data: usersData,
    // error,
  } = useQuery({
    queryKey: ['UsersTeam', tableState.pagination, tableState.filterEmail],
    queryFn: () =>
      getUsers({
        page: tableState.pagination.page + 1,
        pageSize: 1000,
        email: tableState.filterEmail,
      }),
  });

  const {
    isPending: isPendingTeams,
    data: teamsData,
    // error,
  } = useQuery({
    queryKey: ['Teams'],
    queryFn: getAllTeams,
  });

  const promoteUserToTeamManagerMutation = useMutation({
    mutationFn: (userId: string) => promoteUserToTeamManager(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UsersTeam', tableState.pagination, tableState.filterEmail],
      });
      queryClient.invalidateQueries({
        queryKey: ['Teams'],
      });
      toast.success('Utente promosso a Team Manager!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const removeUserFromTeamManagerMutation = useMutation({
    mutationFn: (userId: string) => removeUserFromTeamManager(userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UsersTeam', tableState.pagination, tableState.filterEmail],
      });
      queryClient.invalidateQueries({
        queryKey: ['Teams'],
      });
      toast.success('Utente rimosso come Team Manager!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const addMemberToTeamMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      addMemberToTeam(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UsersTeam', tableState.pagination, tableState.filterEmail],
      });
      queryClient.invalidateQueries({
        queryKey: ['Teams'],
      });
      toast.success('Utente aggiunto al team!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const removeMemberFromTeamMutation = useMutation({
    mutationFn: ({ teamId, userId }: { teamId: string; userId: string }) =>
      removeMemberFromTeam(teamId, userId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UsersTeam', tableState.pagination, tableState.filterEmail],
      });
      queryClient.invalidateQueries({
        queryKey: ['Teams'],
      });
      toast.success('Utente rimosso dal team!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const changeCanReceiveLeadsMutation = useMutation({
    mutationFn: ({
      userId,
      canReceiveLeads,
    }: {
      userId: string;
      canReceiveLeads: boolean;
    }) => changeCanReceiveLeads({ userId, canReceiveLeads }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UsersTeam', tableState.pagination, tableState.filterEmail],
      });
      toast.success('Assegna Lead modificato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const changeCanReceiveLeadsQuintoMutation = useMutation({
    mutationFn: ({
      userId,
      canReceiveLeadsQuinto,
    }: {
      userId: string;
      canReceiveLeadsQuinto: boolean;
    }) => changeCanReceiveLeadsQuinto({ userId, canReceiveLeadsQuinto }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UsersTeam', tableState.pagination, tableState.filterEmail],
      });
      toast.success('Assegna Lead Quinto modificato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const changeCanReceiveLeadsPrestitoMutation = useMutation({
    mutationFn: ({
      userId,
      canReceiveLeadsPrestito,
    }: {
      userId: string;
      canReceiveLeadsPrestito: boolean;
    }) => changeCanReceiveLeadsPrestito({ userId, canReceiveLeadsPrestito }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ['UsersTeam', tableState.pagination, tableState.filterEmail],
      });
      toast.success('Assegna Lead Prestito modificato!');
    },
    onError: (error) => {
      toast.error(error.message || 'Si è verificato un errore, riprova!');
    },
  });

  const onSubmitFilterEmail = (email: string) => {
    setTableState({
      pagination: defaultPageModel,
      filterEmail: email || '',
    });
  };

  const onTeamManagerCheckboxChange = (row: User) => {
    setModalState((prev) => ({
      ...prev,
      userToChangeTeamManager: row,
    }));
  };

  const onConfirmChangeTeamManager = async () => {
    if (!modalState.userToChangeTeamManager) {
      return;
    }

    if (!modalState.userToChangeTeamManager?.team) {
      await promoteUserToTeamManagerMutation.mutateAsync(
        modalState.userToChangeTeamManager.id
      );
      return;
    }

    await removeUserFromTeamManagerMutation.mutateAsync(
      modalState.userToChangeTeamManager.id
    );
  };

  const onTeamChange = (user: User, teamId: string) => {
    const team = teamsData?.find((t) => t.id === teamId);
    if (!team) {
      return;
    }

    setModalState((prev) => ({
      ...prev,
      addMemberToTeam: { team, user },
    }));
  };

  const onRemoveMemberFromTeam = (user: User, teamId: string) => {
    const team = teamsData?.find((t) => t.id === teamId);
    if (!team) {
      return;
    }

    setModalState((prev) => ({
      ...prev,
      removeMemberFromTeam: { team, user },
    }));
  };

  const onConfirmAddMemberToTeam = async () => {
    if (!modalState.addMemberToTeam) {
      return;
    }

    await addMemberToTeamMutation.mutateAsync({
      teamId: modalState.addMemberToTeam.team.id,
      userId: modalState.addMemberToTeam.user.id,
    });
  };

  const onConfirmRemoveMemberFromTeam = async () => {
    if (!modalState.removeMemberFromTeam) {
      return;
    }

    await removeMemberFromTeamMutation.mutateAsync({
      teamId: modalState.removeMemberFromTeam.team.id,
      userId: modalState.removeMemberFromTeam.user.id,
    });
  };

  const onCanReceiveLeadsCheckboxChange = (row: User) => {
    setModalState((prev) => ({
      ...prev,
      userToChangeCanReceiveLeads: row,
    }));
  };

  const onCanReceiveLeadsQuintoCheckboxChange = (row: User) => {
    setModalState((prev) => ({
      ...prev,
      userToChangeCanReceiveLeadsQuinto: row,
    }));
  };

  const onCanReceiveLeadsPrestitoCheckboxChange = (row: User) => {
    setModalState((prev) => ({
      ...prev,
      userToChangeCanReceiveLeadsPrestito: row,
    }));
  };

  const confirmChangeCanReceiveLeads = async () => {
    if (!modalState.userToChangeCanReceiveLeads) {
      return;
    }

    await changeCanReceiveLeadsMutation.mutateAsync({
      userId: modalState.userToChangeCanReceiveLeads.id,
      canReceiveLeads: !modalState.userToChangeCanReceiveLeads.canReceiveLeads,
    });
  };

  const confirmChangeCanReceiveLeadsQuinto = async () => {
    if (!modalState.userToChangeCanReceiveLeadsQuinto) {
      return;
    }

    await changeCanReceiveLeadsQuintoMutation.mutateAsync({
      userId: modalState.userToChangeCanReceiveLeadsQuinto.id,
      canReceiveLeadsQuinto: !modalState.userToChangeCanReceiveLeadsQuinto.canReceiveLeadsQuinto,
    });
  };

  const confirmChangeCanReceiveLeadsPrestito = async () => {
    if (!modalState.userToChangeCanReceiveLeadsPrestito) {
      return;
    }

    await changeCanReceiveLeadsPrestitoMutation.mutateAsync({
      userId: modalState.userToChangeCanReceiveLeadsPrestito.id,
      canReceiveLeadsPrestito: !modalState.userToChangeCanReceiveLeadsPrestito.canReceiveLeadsPrestito,
    });
  };

  const getModalConfig = (modalState: ModalState): ModalConfig | null => {
    if (modalState.userToChangeTeamManager) {
      const isRemoving = modalState.userToChangeTeamManager.team;
      return {
        title: isRemoving ? 'Rimuovi Manager Team' : 'Promuovi a Team Manager',
        description: `Sei sicuro di voler ${
          isRemoving ? 'rimuovere' : 'promuovere'
        } ${modalState.userToChangeTeamManager.name} come Team Manager?`,
        onConfirm: onConfirmChangeTeamManager,
      };
    }

    if (modalState.addMemberToTeam) {
      return {
        title: 'Aggiungi membro al team',
        description: `Sei sicuro di voler aggiungere ${modalState.addMemberToTeam.user.name} al team di ${modalState.addMemberToTeam.team.teamManager.name}?`,
        onConfirm: onConfirmAddMemberToTeam,
      };
    }

    if (modalState.removeMemberFromTeam) {
      return {
        title: 'Rimuovi membro dal team',
        description: `Sei sicuro di voler rimuovere ${modalState.removeMemberFromTeam.user.name} dal team di ${modalState.removeMemberFromTeam.team.teamManager.name}?`,
        onConfirm: onConfirmRemoveMemberFromTeam,
      };
    }

    if (modalState.userToChangeCanReceiveLeads) {
      return {
        title: modalState.userToChangeCanReceiveLeads.canReceiveLeads
          ? 'Disabilita assegnazione Lead'
          : 'Abilita assegnazione Lead',
        description: `Sei sicuro di voler ${
          modalState.userToChangeCanReceiveLeads.canReceiveLeads
            ? 'disabilitare'
            : 'abilitare'
        } l'assegnazione Lead per ${modalState.userToChangeCanReceiveLeads.name}?`,
        onConfirm: confirmChangeCanReceiveLeads,
      };
    }

    if (modalState.userToChangeCanReceiveLeadsQuinto) {
      return {
        title: modalState.userToChangeCanReceiveLeadsQuinto.canReceiveLeadsQuinto
          ? 'Disabilita assegnazione Lead Quinto'
          : 'Abilita assegnazione Lead Quinto',
        description: `Sei sicuro di voler ${
          modalState.userToChangeCanReceiveLeadsQuinto.canReceiveLeadsQuinto
            ? 'disabilitare'
            : 'abilitare'
        } l'assegnazione Lead Quinto per ${modalState.userToChangeCanReceiveLeadsQuinto.name}?`,
        onConfirm: confirmChangeCanReceiveLeadsQuinto,
      };
    }

    if (modalState.userToChangeCanReceiveLeadsPrestito) {
      return {
        title: modalState.userToChangeCanReceiveLeadsPrestito.canReceiveLeadsPrestito
          ? 'Disabilita assegnazione Lead Prestito'
          : 'Abilita assegnazione Lead Prestito',
        description: `Sei sicuro di voler ${
          modalState.userToChangeCanReceiveLeadsPrestito.canReceiveLeadsPrestito
            ? 'disabilitare'
            : 'abilitare'
        } l'assegnazione Lead Prestito per ${modalState.userToChangeCanReceiveLeadsPrestito.name}?`,
        onConfirm: confirmChangeCanReceiveLeadsPrestito,
      };
    }

    return null;
  };

  const modalConfig = getModalConfig(modalState);

  return {
    onSubmitFilterEmail,
    onTeamManagerCheckboxChange,
    onTeamChange,
    onRemoveMemberFromTeam,
    onCanReceiveLeadsCheckboxChange,
    onCanReceiveLeadsQuintoCheckboxChange,
    onCanReceiveLeadsPrestitoCheckboxChange,
    usersData,
    isPending,
    isPendingTeams,
    tableState,
    setTableState,
    teamsData,
    modalConfig,
    setModalState,
  };
};

export default useTeamsPage;
